import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import GenericTable from "../../GenericTable/GenericTable";
import { useNavigate } from "react-router-dom";
import config from "../../../../config";

const userColumns = [
  { id: "user_name", label: "Username" },
  { id: "email_id", label: "Email" },
  { id: "phone_number", label: "Phone Number" },
  { id: "last_login", label: "Last Login" },
];

function UserManagementList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Modal states
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ✅ Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);



  const handleDeleteClick = (row) => {
    // ✅ Only allow delete if role is NOT superadmin
    if (row.role === "superadmin") {
      return;
    }
    setSelectedRow(row);
    setOpen(true);
  };

  // ✅ Check if delete should be disabled for a row
  const isDeleteDisabled = (row) => {
    return row.role === "superadmin";
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow) return;

    try {
      // Call backend hard delete
      const res = await fetch(`${config.API_BASE_URL_V1}/admin/user/${selectedRow.admin_user_id}`, {
        method: "DELETE",
        headers: config.getHeaders(),
      });
      const json = await res.json();

      if (!res.ok || json?.success === false) {
        console.error("Delete failed:", json?.error?.details || json?.message);
        return;
      }

      // ✅ Refresh the page after successful delete
      window.location.reload();
    } catch (e) {
      console.error("Network error deleting user:", e?.message || e);
    } finally {
      handleClose();
    }
  };

  const fetchUsers = useCallback(async (pageNum = page, rowSize = limit) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL_V1}/admin/user/list?page=${pageNum}&limit=${rowSize}`,
        { headers: config.getHeaders() }
      );
      const json = await res.json();

      console.log("[ADMIN USER LIST] API Response:", json);

      if (!res.ok || json?.success === false) {
        console.error("[ADMIN USER LIST] API call failed:", json?.message || json?.error);
        setRows([]);
        setError(json?.message || "Failed to fetch users");
        return;
      }

      const data = Array.isArray(json.data) ? json.data : [];

      // ✅ normalize fields the table expects
      const normalized = data.map((u) => {
        const rawLastLogin = u.last_login;
        let formattedLastLogin = "Never";

        if (rawLastLogin) {
          try {
            const d = new Date(rawLastLogin);
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const year = d.getFullYear();
            const timePart = d.toTimeString().split(" ")[0];
            formattedLastLogin = `${month}-${day}-${year} ${timePart}`;
          } catch {
            formattedLastLogin = rawLastLogin;
          }
        }

        return {
          admin_user_id: u.admin_user_id,
          user_name: u.user_name || "N/A",
          email_id: u.email_id || "N/A",
          phone_number: u.phone_number || "N/A",
          last_login: formattedLastLogin,
          role: u.role, // ✅ Include role for delete button logic
        };
      });

      setRows(normalized);
      setTotalCount(json?.pagination?.total_records || 0);
      setError(null);
    } catch (_e) {
      console.error("[ADMIN USER LIST] Exception:", _e);
      setRows([]);
      setError("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchUsers();
  }, [page, limit, fetchUsers]);

  if (error) {
    return (
      <div style={{ padding: "2rem", color: "red" }}>
        {error}
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <CircularProgress />
        </div>
      )}
      {!loading && (
        <GenericTable
          title=""
          columns={userColumns}
          rows={rows}
          isServerSide={true}
          currentPage={page - 1}
          rowsPerPage={limit}
          totalCount={totalCount}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1);
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setLimit(newRowsPerPage);
            setPage(1);
          }}
          showView={false}
          showEdit={true}
          showDelete={true}
          isDeleteDisabled={isDeleteDisabled}
          onEdit={(row) => navigate(`/user-management/${row.admin_user_id}/edit-user`)}
          onDelete={handleDeleteClick}
          customHeaderButtons={[
            {
              label: "Add New User",
              icon: "add",
              onClick: () => navigate(`/user-management/add-user`)
            }
          ]}
        />
      )}

      {/* ✅ Delete Confirmation Modal */}
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.user_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="secondary">
            No
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserManagementList;
